import { Box, Divider, Group, Text, Tooltip } from '@byecode/ui'
import type { TypeInstanceMap } from '@lighthouse/core'
import type { ExtractParams, FlowNode, NodeTypes, ParamExtractionActionConfig } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'
import OutputVariable from '@/containers/FlowDetail/components/OutputVariable'
import { innerTypeOptions } from '@/containers/FlowDetail/constants'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import SelectModel from '../LLMActionConfigure/SelectModel'
import ExtractQueryParams from './ExtractQueryParams'
import * as SC from './styles'

type ParameterExtractActionConfigureProps = {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: ParamExtractionActionConfig
    onChange: (value: ParamExtractionActionConfig) => void
}

export const typeName = (type: TypeInstanceMap) => {
    const item = innerTypeOptions.find(item => item.value === type)

    return item?.label
}

export const ParameterExtractActionConfigure: React.FC<ParameterExtractActionConfigureProps> = ({ allParentNodes, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const { args, input, model, prompt } = value

    const { type } = useFlowContainerContext()
    const isInFlow = !!type

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                      allUpstreamNodes: allParentNodes,
                      dataSourceList
                  })
                : undefined,
        [allParentNodes, dataSourceList, isInFlow, workflowList]
    )

    const userOption = useGetUserRecordOptions()

    const variableList = useMemo(
        () =>
            args?.map((item: ExtractParams) => {
                return {
                    id: item.id,
                    label: item.name,
                    type: item.innerType,
                    desc: item.description
                }
            }),
        [args]
    )

    return (
        <SC.Container>
            <Group label="模型">
                <Box py={4} px={8}>
                    <SelectModel
                        value={model}
                        disabledModels={['QWEN_VL_MAX', 'QWEN_VL_PLUS', 'QWEN_VL_OCR']}
                        onChange={nextModel => onChange({ ...value, model: nextModel })}
                    />
                </Box>
            </Group>

            <Divider />

            <Group label="输入变量">
                <Box px={8} pt={4}>
                    <NodeFieldPreview
                        innerTypes={getInputInnerTypeMatch('TEXT')}
                        placeholder="在这里写你的提示语"
                        autoHeight
                        value={input}
                        upstreamOptions={upstreamOptions}
                        onBlur={nextInput => onChange({ ...value, input: nextInput })}
                        userOption={userOption}
                        dataSourceList={dataSourceList}
                    />
                </Box>
            </Group>

            <Divider />

            <ExtractQueryParams options={innerTypeOptions} value={args} onChange={nextArgs => onChange({ ...value, args: nextArgs })} />

            <Divider />

            <Group
                label={
                    <Tooltip
                        title={
                            <SC.TooltipContent>
                                你可以输入额外的附加指
                                <br />
                                令，帮助参数提取器理解如
                                <br />
                                何提取参数
                            </SC.TooltipContent>
                        }
                    >
                        <Text>提示词</Text>
                    </Tooltip>
                }
            >
                <Box pt={4} px={8}>
                    <NodeFieldPreview
                        innerTypes={getInputInnerTypeMatch('TEXT')}
                        placeholder="在这里写你的提示语"
                        autoHeight
                        value={prompt}
                        upstreamOptions={upstreamOptions}
                        onBlur={nextPrompt => onChange({ ...value, prompt: nextPrompt })}
                        userOption={userOption}
                        dataSourceList={dataSourceList}
                    />
                </Box>
            </Group>

            <OutputVariable options={variableList} />
        </SC.Container>
    )
}
